<template>
  <v-dialog :value="value" :max-width="dialog_max_width" @input="close">
    <v-card>
      <v-card-title class="headline">
        {{ dialog_title }}
      </v-card-title>
      <v-card-subtitle v-if="item" class="pt-1">
        {{
          $t("payment.academic_period", {
            semester: item.semester,
            year: item.year,
          })
        }}
      </v-card-subtitle>

      <v-form
        v-if="type === 'other'"
        ref="form"
        v-model="valid"
        @submit.prevent="submit"
      >
        <v-card-text :class="{ 'pb-0': readonly }">
          <v-alert v-if="!readonly" type="info" text dense>
            {{ $t("payment.edit_recalculate_note") }}
          </v-alert>

          <div v-if="readonly" class="fee-detail-list">
            <div
              v-for="field in fields"
              :key="field.key"
              class="fee-detail-row"
            >
              <span class="fee-detail-label">{{ field.label }}</span>
              <span class="fee-detail-amount">
                {{ display_amount(form[field.key]) }}
                <span class="ml-1">{{ $t("payment.baht") }}</span>
              </span>
            </div>
          </div>

          <v-text-field
            v-for="field in readonly ? [] : fields"
            :key="field.key"
            v-model="form[field.key]"
            class="fee-amount-input"
            :label="field.label"
            :suffix="$t('payment.baht')"
            :rules="amount_rules"
            type="number"
            min="0"
            step="1"
            inputmode="numeric"
            outlined
            dense
          />
        </v-card-text>

        <v-card-actions
          :class="
            readonly
              ? 'fee-detail-actions px-6 pt-3 pb-4'
              : 'pb-5 px-6'
          "
        >
          <v-spacer />
          <v-btn
            v-if="readonly"
            color="#ff5722"
            outlined
            :disabled="loading"
            @click="close(false)"
          >
            {{ $t("payment.close") }}
          </v-btn>
          <v-btn
            v-if="!readonly"
            color="#ff5722"
            dark
            depressed
            type="submit"
            :loading="loading"
          >
            {{ $t("payment.save") }}
          </v-btn>
          <v-btn
            v-if="!readonly"
            class="ml-2"
            color="#ff5722"
            outlined
            :disabled="loading"
            @click="close(false)"
          >
            {{ $t("payment.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-form>

      <template v-else>
        <v-card-text class="pb-0">
          <v-alert type="info" text dense>
            {{ penalty_action_message }}
          </v-alert>
        </v-card-text>
        <v-card-text class="pt-0">
          {{ $t("payment.penalty_saved_status") }}
          <strong>{{ penalty_saved_status }}</strong>
        </v-card-text>
        <v-card-actions class="penalty-charge-actions pb-6 px-6">
          <v-btn
            :color="loading || penalty_is_exempt ? undefined : 'success'"
            :dark="!loading && !penalty_is_exempt"
            depressed
            :disabled="loading || penalty_is_exempt"
            @click="select_penalty_action(true)"
          >
            {{ exempt_penalty_label }}
          </v-btn>
          <v-btn
            :color="loading || !penalty_is_exempt ? undefined : 'error'"
            :dark="!loading && penalty_is_exempt"
            depressed
            :disabled="loading || !penalty_is_exempt"
            @click="select_penalty_action(false)"
          >
            {{ add_penalty_label }}
          </v-btn>
          <v-btn
            color="#ff5722"
            outlined
            :disabled="loading"
            @click="close(false)"
          >
            {{ $t("payment.close") }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "PaymentFeeEditDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    level_id: {
      type: Number,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    valid: false,
    form: {
      subsidy_fee: "0",
      thesis_fee: "0",
      compre_fee: "0",
      qe_fee: "0",
    },
  }),
  computed: {
    dialog_max_width() {
      if (this.type !== "other") return 560;
      return this.readonly ? 460 : 560;
    },
    dialog_title() {
      if (this.type === "other") {
        return this.readonly
          ? this.$t("payment.other_fee_details")
          : this.$t("payment.edit_other_fees");
      }
      return this.type === "pay_charge"
        ? this.$t("payment.edit_pay_charge")
        : this.$t("payment.edit_late_charge");
    },
    penalty_action_message() {
      return this.type === "pay_charge"
        ? this.$t("payment.select_pay_charge_action")
        : this.$t("payment.select_late_charge_action");
    },
    penalty_skip_key() {
      return this.type === "pay_charge"
        ? "skip_pay_charge"
        : "skip_late_charge";
    },
    penalty_is_exempt() {
      const value = this.item?.[this.penalty_skip_key];
      return value === true || value === 1 || value === "1";
    },
    penalty_saved_status() {
      return this.$t(
        this.penalty_is_exempt
          ? "payment.penalty_status_exempt"
          : "payment.penalty_status_charge"
      );
    },
    exempt_penalty_label() {
      return this.type === "pay_charge"
        ? this.$t("payment.exempt_pay_charge")
        : this.$t("payment.exempt_late_charge");
    },
    add_penalty_label() {
      return this.type === "pay_charge"
        ? this.$t("payment.add_pay_charge")
        : this.$t("payment.add_late_charge");
    },
    fields() {
      if (this.level_id === 1) {
        return [
          { key: "subsidy_fee", label: this.$t("payment.subsidy_fee") },
        ];
      }
      return [
        { key: "thesis_fee", label: this.$t("payment.thesis_fee") },
        { key: "compre_fee", label: this.$t("payment.compre_fee") },
        { key: "qe_fee", label: this.$t("payment.qe_fee") },
      ];
    },
    amount_rules() {
      return [
        (value) =>
          (value !== null && value !== undefined && value !== "") ||
          this.$t("payment.amount_required"),
        (value) =>
          /^\d+$/.test(String(value ?? "")) ||
          this.$t("payment.amount_nonnegative_integer"),
      ];
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.initialize_form();
    },
  },
  methods: {
    raw_amount(value) {
      const amount = Number(String(value ?? "0").replace(/,/g, ""));
      return Number.isFinite(amount) ? String(Math.trunc(amount)) : "0";
    },
    display_amount(value) {
      const amount = Number(String(value ?? "0").replace(/,/g, ""));
      if (!Number.isFinite(amount)) return "-";
      return amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    initialize_form() {
      const form = {};
      this.fields.forEach((field) => {
        form[field.key] = this.raw_amount(this.item?.[field.key]);
      });
      this.form = form;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    close(open = false) {
      if (this.loading) return;
      this.$emit("input", open);
    },
    submit() {
      if (this.readonly || !this.$refs.form?.validate()) return;

      const values = {};
      this.fields.forEach((field) => {
        values[field.key] = Number(this.form[field.key]);
      });
      this.$emit("save", values);
    },
    select_penalty_action(isExempt) {
      this.$emit("save", { is_exempt: isExempt });
    },
  },
};
</script>

<style lang="scss" scoped>
.fee-amount-input {
  width: 100%;
}

.fee-detail-list {
  width: 100%;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.fee-detail-actions {
  width: 100%;
}

.penalty-charge-actions {
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.fee-detail-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  min-height: 48px;
  padding: 10px 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.fee-detail-label {
  color: rgba(0, 0, 0, 0.65);
}

.fee-detail-amount {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  white-space: nowrap;
}

@media (max-width: 420px) {
  .fee-detail-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
